import React, { useState, useMemo, useEffect } from "react";
import "./PersonalizePage.css";
import products from "../hair_products.json";
import { getProductImage, isSkinCategory } from "../constants";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

const HAIR_FEEDBACK_LABELS = [
    "Saçımı yağlandırdı 🛢️",
    "Saç dökülmesine neden oldu 💔",
    "Kepeği azalttı ❄️",
    "Hacim kazandırdı 📈",
    "Saç derisini rahatlattı 🧖‍♀️",
    "Kuruluk yaptı 🌵",
];

const SKIN_FEEDBACK_LABELS = [
    "Cildimi nemlendirdi 💧",
    "Sivilce yaptı 😡",
    "Yağlı hissettirdi 🛢️",
    "Cildi rahatlattı 🧖‍♀️",
    "Alerjik reaksiyon yaptı 🚨",
    "Hassasiyeti azalttı 🌿",
];

export default function PersonalizePage() {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [note, setNote] = useState("");
    const [selectedLabels, setSelectedLabels] = useState([]);
    const [rating, setRating] = useState(0);
    const [feedbackList, setFeedbackList] = useState([]);
    const navigate = useNavigate();

    const browserId = useMemo(() => {
        let id = localStorage.getItem("browserId");
        if (!id) {
            id = crypto.randomUUID();
            localStorage.setItem("browserId", id);
        }
        return id;
    }, []);

    const allProducts = useMemo(() => Object.values(products).flat(), []);

    const filteredProducts = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return [];
        return allProducts
            .filter((item) => {
                const brand = item.brand?.toLowerCase() || "";
                const name = item.name?.toLowerCase() || "";
                return brand.includes(query) || name.includes(query);
            })
            .slice(0, 10);
    }, [searchQuery, allProducts]);

    useEffect(() => {
        const fetchPersonalizations = async () => {
            try {
                const response = await fetch(`https://api.whatsin-app.com/personalization/${browserId}`);
                if (!response.ok) throw new Error("Failed to fetch personalizations");
                const data = await response.json();
                setFeedbackList(data);
            } catch (error) {
                console.error("Error fetching personalizations:", error);
            }
        };
        fetchPersonalizations();
    }, [browserId]);

    const getFeedbackLabels = () => {
        if (!selectedProduct) return [];
        const category = Object.keys(products).find((key) =>
            products[key].some((p) => p.barcode === selectedProduct.barcode)
        );
        return isSkinCategory(category) ? SKIN_FEEDBACK_LABELS : HAIR_FEEDBACK_LABELS;
    };


    const handleAddFeedback = async () => {
        if (!selectedProduct) {
            alert("Lütfen tüm alanları doldurun.");
            return;
        }
        const payload = {
            product_barcode: selectedProduct.barcode,
            browserId,
            note,
            labels: selectedLabels,
            warn_for_similar: true,
        };
        try {
            const response = await fetch("https://api.whatsin-app.com/personalization/save", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error("Failed to save personalization");
            const result = await response.json();
            const newFeedback = {
                id: result.id,
                barcode: selectedProduct.barcode,
                note,
                labels: selectedLabels.join(",")
            };
            setFeedbackList([newFeedback, ...feedbackList]);
            resetForm();
        } catch (error) {
            console.error("Error saving personalization:", error);
        }
    };

    const handleDeleteFeedback = async (feedbackId) => {
        try {
            const response = await fetch(`https://api.whatsin-app.com/personalization/delete/${feedbackId}`, {
                method: "DELETE",
            });
            if (!response.ok) throw new Error("Failed to delete personalization");

            setFeedbackList(feedbackList.filter((f) => f.id !== feedbackId));
        } catch (error) {
            console.error("Error deleting personalization:", error);
        }
    };

    const resetForm = () => {
        setSelectedProduct(null);
        setSearchQuery("");
        setNote("");
        setSelectedLabels([]);
        setRating(0);
    };

    const handleLabelToggle = (label) => {
        if (selectedLabels.includes(label)) {
            setSelectedLabels(selectedLabels.filter((l) => l !== label));
        } else {
            setSelectedLabels([...selectedLabels, label]);
        }
    };

    return (
        <div className="pre-container">
            <header className="pre-header">
                <h1 className="pre-title">✨ Geri Bildirimin, Küçük Sırrın! ✨</h1>
                <p className="pre-subtitle">
                    Ürün deneyimlerini kaydet, böylece benzer içeriklere sahip ürünlerden
                    kaçınmanı veya aradığın bileşenleri içeren alternatifleri keşfetmeni
                    sağlayalım ✨
                </p>
            </header>
            <main className="pre-content">
                <section>
                    <h2 className="new-feedback-title">Yeni Geri Bildirim Ekle</h2>
                    {!selectedProduct && (
                        <>
                            <div className="search-container">
                                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                <input
                                    className="search-bar"
                                    type="text"
                                    placeholder="Ürün adı veya marka..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />

                            </div>
                            {filteredProducts.length > 0 && (
                                <div className="pre-search-list">
                                    {filteredProducts.map((item, index) => {
                                        const category = Object.keys(products).find((key) =>
                                            products[key].some((p) => p.barcode === item.barcode)
                                        );
                                        return (
                                            <div
                                                key={item.barcode || index}
                                                onClick={() => setSelectedProduct(item)}
                                                className="pre-search-item"
                                            >
                                                <img
                                                    src={getProductImage(category, item.barcode, item.id)}
                                                    alt={item.name}
                                                    className="pre-search-item-img"
                                                />
                                                <div className="pre-search-item-text">
                                                    <p className="pre-search-item-name">{item.name}</p>
                                                    <p className="pre-search-item-brand">{item.brand}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                    {selectedProduct && (
                        <div className="pre-feedback-form">
                            <div className="pre-selected-product">
                                <img
                                    src={getProductImage(
                                        Object.keys(products).find((key) =>
                                            products[key].some(
                                                (p) => p.barcode === selectedProduct.barcode
                                            )
                                        ),
                                        selectedProduct.barcode,
                                        selectedProduct.id
                                    )}
                                    alt={selectedProduct.name}
                                    className="pre-selected-product-img"
                                />
                                <div className="pre-selected-product-info">
                                    <h4>{selectedProduct.name}</h4>
                                    <p>{selectedProduct.brand}</p>
                                    <button
                                        className="pre-remove-btn"
                                        onClick={() => setSelectedProduct(null)}
                                    >
                                        Kaldır
                                    </button>
                                </div>
                            </div>
                            <div className="pre-rating-container">
                                <p>Ürünü Oyla:</p>
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                        key={star}
                                        className={`pre-star ${star <= rating ? "active" : ""}`}
                                        onClick={() => setRating(star)}
                                    >
                                        ⭐
                                    </span>
                                ))}
                            </div>
                            <textarea
                                placeholder="Deneyiminizi paylaşın..."
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows="3"
                            ></textarea>
                            <div className="pre-tags-container">
                                <p>Deneyiminizi Etiketleyin:</p>
                                {getFeedbackLabels().map((label) => (
                                    <span
                                        key={label}
                                        className={`pre-tag ${selectedLabels.includes(label) ? "selected" : ""}`}
                                        onClick={() => handleLabelToggle(label)}
                                    >
                                        {label}
                                    </span>
                                ))}
                            </div>
                            <div className="button-container">
                                <button onClick={handleAddFeedback} className="pre-save-btn">
                                    Geri Bildirim Ekle
                                </button>
                            </div>
                        </div>
                    )}
                </section>
                <section className="section">
                    <h2>Önceki Geri Bildirimlerin</h2>
                    {feedbackList.length === 0 ? (
                        <p className="pre-empty-feedback">Henüz geri bildirim yok.</p>
                    ) : (
                        feedbackList.map((item) => {
                            const product = Object.values(products)
                                .flat()
                                .find((p) => p.barcode === item.barcode);
                            return (
                                <div key={item.id} className="pre-feedback-card">
                                    <img
                                        src={product?.image || "placeholder-image-url"}
                                        alt={product?.name || "Product Name"}
                                        className="pre-feedback-card-img"
                                        onClick={() => navigate(`/product/${product?.barcode}`)}
                                    />
                                    <div className="pre-feedback-card-details">
                                        <h4>{product?.name || "Product Name"}</h4>
                                        <p>{product?.brand || "Brand"}</p>
                                        {item.rating && (
                                            <p>
                                                Puan: <span className="pre-rating">{"⭐".repeat(item.rating || 0)}</span>
                                            </p>
                                        )}
                                        <p>{item.note}</p>
                                        <div>
                                            {typeof item?.labels === "string" &&
                                                item.labels.split(",").map((tag) => (
                                                    <span key={tag.trim()} className="pre-feedback-tag">
                                                        {tag.trim()}
                                                    </span>
                                                ))}
                                        </div>
                                        <button
                                            className="pre-delete-btn"
                                            onClick={() => handleDeleteFeedback(item.id)}
                                        >
                                            Sil
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </section>

            </main>
        </div>
    );
}
